import { useAuth } from '../context/AuthContext';
import { roleConfig, roleCanSeeMoney, roleCanSeeNav } from '../config/rolePermissions';

const normalize = (role) => String(role || '').toUpperCase();

/**
 * Permission validation hook
 * Provides functions to check if user has specific permissions
 * Usage: const { canDeleteCourse } = usePermissions();
 *
 * Role comparisons are case-insensitive — backend may return any of
 * 'ADMIN' / 'admin' / 'super_admin' depending on the auth path.
 */
export function usePermissions() {
  const { user, staffRole } = useAuth();
  const role = normalize(user?.role);

  // Effective CRM functional role (from team membership). While it's still
  // resolving (null), or if resolution failed, default to the LEAST-privilege
  // STAFF role — never ADMIN. Account-level admins are unaffected: isAdmin()
  // below reads user.role, not this. Fail closed. See B2B_CRM_ROADMAP.md 0.1.
  const effectiveRole = staffRole || 'STAFF';

  const isAdmin = () => role === 'ADMIN' || role === 'SUPERADMIN' || role === 'SUPER_ADMIN';
  const isTeacher = () => role === 'TEACHER' || role === 'MENTOR';
  const isAdminOrTeacher = () => isAdmin() || isTeacher();
  const isStudent = () => role === 'STUDENT';

  // Course permissions
  const canCreateCourse = () => isAdminOrTeacher();
  const canEditCourse = (courseOwnerId) => {
    if (isAdmin()) return true;
    // Teachers can only edit their own courses
    return isTeacher() && user?.id === courseOwnerId;
  };
  const canDeleteCourse = (courseOwnerId) => {
    if (isAdmin()) return true;
    return isTeacher() && user?.id === courseOwnerId;
  };
  const canPublishCourse = () => isAdminOrTeacher();
  const canFeatureCourse = () => isAdmin();

  // Module permissions
  const canCreateModule = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canEditModule = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canDeleteModule = (courseOwnerId) => canDeleteCourse(courseOwnerId);

  // Lesson permissions
  const canCreateLesson = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canEditLesson = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canDeleteLesson = (courseOwnerId) => canDeleteCourse(courseOwnerId);

  // Assignment/Quiz permissions
  const canCreateAssignment = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canEditAssignment = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canDeleteAssignment = (courseOwnerId) => canDeleteCourse(courseOwnerId);
  const canGradeAssignment = (_courseOwnerId) => isAdminOrTeacher();

  // Student permissions
  const canViewStudents = () => isAdminOrTeacher();
  const canViewStudentProgress = () => isAdminOrTeacher();
  const canDeleteStudent = () => isAdmin();

  // Enrollment permissions
  const canEnrollStudents = () => isAdminOrTeacher();
  const canRemoveEnrollment = () => isAdminOrTeacher();

  // Certificate permissions
  const canIssueCertificate = () => isAdminOrTeacher();
  const canRevokeCertificate = () => isAdmin();

  // Analytics permissions
  const canViewAnalytics = () => isAdminOrTeacher() || roleCanSeeNav(effectiveRole, 'analytics');
  // Money gate — revenue, order amounts, payment analytics. Admin + Sales only
  // by default (see src/config/rolePermissions.js).
  const canViewMoney = () => isAdmin() || roleCanSeeMoney(effectiveRole);
  const canViewRevenue = () => canViewMoney();
  const canSeeNav = (navKey) => isAdmin() || roleCanSeeNav(effectiveRole, navKey);

  // Q&A permissions
  const canModerateQA = (courseOwnerId) => canEditCourse(courseOwnerId);
  const canDeleteQAThread = (courseOwnerId) => canModerateQA(courseOwnerId);
  const canDeleteQAReply = (courseOwnerId) => canModerateQA(courseOwnerId);

  // Discount permissions
  const canCreateDiscount = () => isAdminOrTeacher();
  const canEditDiscount = () => isAdminOrTeacher();
  const canDeleteDiscount = () => isAdmin();

  // Return all permission functions
  return {
    // User info
    isAdmin,
    isTeacher,
    isStudent,
    isAdminOrTeacher,
    user,
    staffRole: effectiveRole,
    roleConfig: () => roleConfig(effectiveRole),
    canViewMoney,
    canSeeNav,

    // Course
    canCreateCourse,
    canEditCourse,
    canDeleteCourse,
    canPublishCourse,
    canFeatureCourse,

    // Module
    canCreateModule,
    canEditModule,
    canDeleteModule,

    // Lesson
    canCreateLesson,
    canEditLesson,
    canDeleteLesson,

    // Assignment
    canCreateAssignment,
    canEditAssignment,
    canDeleteAssignment,
    canGradeAssignment,

    // Students
    canViewStudents,
    canViewStudentProgress,
    canDeleteStudent,
    canEnrollStudents,
    canRemoveEnrollment,

    // Certificates
    canIssueCertificate,
    canRevokeCertificate,

    // Analytics
    canViewAnalytics,
    canViewRevenue,

    // Q&A
    canModerateQA,
    canDeleteQAThread,
    canDeleteQAReply,

    // Discounts
    canCreateDiscount,
    canEditDiscount,
    canDeleteDiscount
  };
}

/**
 * Helper component to conditionally render based on permission
 * Usage: <PermissionGate permission={(p) => p.canDeleteCourse(courseId)}>Delete button</PermissionGate>
 */
export function PermissionGate({ children, permission }) {
  const permissions = usePermissions();
  const hasPermission = permission ? permission(permissions) : false;

  if (!hasPermission) {
    return null;
  }

  return children;
}
